import {
  AppShell,
  AppShellHeader,
  Container,
  Group,
  Image,
  ScrollArea,
  UnstyledButton,
  useMantineColorScheme
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../../client-redux/login';
import { clearSidebar, updateActiveTab } from '../../client-redux/sidebar';
import { useCallback, useEffect, useMemo } from 'react';
import userLogo from '../../../assets/img/user-profile.svg';
import exitLogo from '../../../assets/img/exit.svg';
import { ViewMapProps } from '../../utils/view-map-props';
import zarphInitial from '../../../assets/img/zarph_Z_logo_white.svg';
import zarphLogo from '../../../assets/img/Versão_horizontal_branco_sem_assinatura.svg';
import Background from '../util/background';
import { IconArrowBarToLeft, IconMenu2 } from '@tabler/icons-react';
import './styles.scss';

interface ApshellContentProps {
  content: any;
  showLogo?: boolean;
  autoCollapse?: boolean;
}

const Sidebar: React.FC<ApshellContentProps> = ({ content, showLogo = false, autoCollapse = false }) => {
  const location = useLocation();
  const views = useSelector<AppState, ViewMapProps[]>(state => state.sidebar.views);
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [opened, { toggle }] = useDisclosure();

  const isDesktop = useMediaQuery('(min-width: 576px)', false);
  const { colorScheme } = useMantineColorScheme({ keepTransitions: true });
  const activeTab = useSelector<AppState, string>(state => state.sidebar.activeTab);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', colorScheme);
  }, [colorScheme]);

  const logoutOper = useCallback(() => {
    dispatch(logout());
    dispatch(clearSidebar());
    navigate('/login');
  }, [dispatch, navigate]);

  const profileItems = useMemo(
    () => [
      {
        key: 'profile',
        label: lang.USER_PROFILE,
        link: '/profile',
        icon: userLogo,
        onClick: () => dispatch(updateActiveTab(lang.USER_PROFILE))
      },
      { key: 'logout', label: lang.GLOBAL_LOGOUT, link: '/login', icon: exitLogo, onClick: logoutOper }
    ],
    [dispatch, lang, logoutOper]
  );

  // Map of child route patterns to their parent menu path
  const childToParentMap: Record<string, string> = {
    '/helpdesk/ticket/:mode/:ticketID': '/helpdesk/tickets',
    '/helpdesk/ticket/:mode/': '/helpdesk/tickets'
  };

  // Helper to get active tab label from path
  const getLabelFromPath = (path: string) => {
    const parentPath = Object.keys(childToParentMap).find(pattern => matchPath({ path: pattern, end: true }, path));
    const effectivePath = parentPath ? childToParentMap[parentPath] : path;
    const foundView = views.find(view => view.link === effectivePath);
    return foundView ? foundView.label : '';
  };

  useEffect(() => {
    const currentLabel = getLabelFromPath(location.pathname);

    if (location.pathname === '/profile') {
      if (activeTab !== lang.USER_PROFILE) {
        dispatch(updateActiveTab(lang.USER_PROFILE));
      }
    } else if (currentLabel && currentLabel !== activeTab) {
      dispatch(updateActiveTab(currentLabel));
    } else if (!currentLabel && activeTab) {
      dispatch(updateActiveTab(''));
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, views, dispatch, lang.USER_PROFILE]);

  // HEADER
  const renderHeader = useMemo(
    () =>
      isDesktop && (
        <div className={`navbar-header ${showLogo ? 'with-logo' : 'no-logo'}`}>
          {showLogo && (
            <div className="logo-box">
              <Image className={opened ? 'logo' : 'initial'} src={opened ? zarphLogo : zarphInitial} />
            </div>
          )}
          <div className="open-close-button" onClick={toggle}>
            {opened ? <IconArrowBarToLeft stroke={2} /> : <IconMenu2 stroke={2} />}
          </div>
        </div>
      ),
    [isDesktop, opened, toggle, showLogo]
  );

  // MENU
  const renderMenu = useMemo(
    () => (
      <ScrollArea className="menu" scrollbarSize={7} scrollHideDelay={500}>
        {views.map((element, index) => (
          <UnstyledButton
            component={Link}
            to={element.link}
            onClick={() => {
              dispatch(updateActiveTab(element.label));
              if (autoCollapse && opened) toggle();
            }}
            key={index}
            className={element.label === activeTab ? 'tab-selected mantine-focus-auto' : 'mantine-focus-auto'}
          >
            <div className="icon-container">
              <Image src={element.icon} fit="contain" className="icon-img" />
            </div>
            {opened && element.label && <span>{lang[element.label]}</span>}
          </UnstyledButton>
        ))}
      </ScrollArea>
    ),
    [views, lang, opened, activeTab, dispatch, toggle, autoCollapse]
  );

  // PROFILE
  const renderProfileSection = useMemo(
    () => (
      <div className="profile-options">
        {profileItems.map(item => (
          <UnstyledButton
            key={item.key}
            component={Link}
            to={item.link}
            onClick={() => {
              item.onClick?.();
              if (autoCollapse && opened) toggle();
            }}
            className={activeTab === item.label ? 'tab-selected mantine-focus-auto' : 'mantine-focus-auto'}
          >
            <Group>
              <div className="icon-container">
                <Image src={item.icon} fit="contain" className="icon-img" />
              </div>
              {opened && <span>{item.label}</span>}
            </Group>
          </UnstyledButton>
        ))}
        <span className="react-version">{`v${process.env.REACT_APP_VERSION}`}</span>
      </div>
    ),
    [profileItems, activeTab, opened, toggle, autoCollapse]
  );

  // COLLAPSED NAVBAR
  const collapsedNavbar = (
    <Container className="collapsed-navbar">
      <div className="navbar-sections">
        {renderHeader}
        <div className="menu-profile-separator" />
        {renderMenu}
        <div className="menu-profile-separator" />
        {renderProfileSection}
      </div>
    </Container>
  );

  return (
    <AppShell
      data-opened={opened ? 'true' : 'false'}
      navbar={{
        width: 0,
        breakpoint: 'sm',
        collapsed: { mobile: !opened, desktop: !opened }
      }}
    >
      {/* Mobile Header */}
      {!isDesktop && (
        <AppShellHeader>
          <div className="open-close-button" onClick={toggle}>
            {opened ? <IconArrowBarToLeft stroke={2} /> : <IconMenu2 stroke={2} />}
          </div>
          <div className="logo-box">{<Image src={zarphLogo} />}</div>
        </AppShellHeader>
      )}

      {/* Sidebar for Desktop */}
      {isDesktop ? (
        opened ? (
          <AppShell.Navbar>
            <div className="navbar-sections">
              {renderHeader}
              <div className="menu-profile-separator" />
              {renderMenu}
              <div className="menu-profile-separator" />
              {renderProfileSection}
            </div>
          </AppShell.Navbar>
        ) : (
          collapsedNavbar
        )
      ) : (
        <AppShell.Navbar data-collapsed={opened ? 'false' : 'true'}>
          <div className="navbar-sections">
            {renderHeader}
            <div className="menu-profile-separator" />
            {renderMenu}
            <div className="menu-profile-separator" />
            {renderProfileSection}
          </div>
        </AppShell.Navbar>
      )}

      <AppShell.Main>
        <Background>{content}</Background>
      </AppShell.Main>
    </AppShell>
  );
};

export default Sidebar;
